import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import { isEmpty, isNil } from 'ramda'

import type {
  AdditionalDataAPI,
  ConditionsSectionCustom,
  Footer as FooterResponse,
  GlobalResponse,
  NavigationResponse,
  PageComponentsItem,
  PageContentSectionsItem,
  SharedSeoComponent,
} from '@twisto/api-cms'
import { getEnvConfig } from '@twisto/base/env-config'

import type {
  ComponentType,
  ComponentsType,
  SectionType,
} from 'homepage/components'
import { ArticleComponents, Footer, PageSections } from 'homepage/components'
import { Seo } from 'homepage/components/atoms'
import { Header } from 'homepage/components/molecules/header'
import { Navigation } from 'homepage/components/navigation'
import { AdditionalDataProvider } from 'homepage/libs/context'
import {
  CATEGORY_TO_INCLUDE_PARAM,
  type EshopCategory,
  fetchDeferredCalcConfig,
  fetchInstallmentsCalcConfig,
  getBenefits,
  getConditionsPage,
  getConditionsRootPage,
  getConditionsSection,
  getEshops,
  getMediaArticles,
  getNavigation,
  getPage,
  getStaticTexts,
} from 'homepage/utils/api'

import { RewriteSlugs, getHeader } from '../utils'
import { resolveFooterAutoRatesConfigData } from '../utils/auto-rates/footer'
import {
  fetchAutoRatesRepresentativeExamplesData,
  getAutoRatesComponentConfig,
  replaceAutoRatesPlaceholders,
} from '../utils/auto-rates/pages'

type ServerSideProps = {
  navigation?: NavigationResponse
  footer?: FooterResponse
  sections?: SectionType[]
  components?: ComponentType[]
  seo?: SharedSeoComponent
  pageContext: {
    path?: string | string[]
    type?: 'conditions-page' | 'page'
    title?: string
    subtitle?: string
    isRoot?: boolean
  }
  additionalData: AdditionalDataAPI
  sidebar?: ConditionsSectionCustom[]
}

export type DynamicPageProps = ServerSideProps & {
  global: GlobalResponse | null
  additionalDataUrl?: string
}

const ConditionsPageLayout = dynamic(
  () => import('../components/conditions-page-layout'),
  {
    ssr: false,
  }
)

const DynamicPage = ({
  additionalData,
  components,
  footer,
  global,
  navigation,
  pageContext,
  sections,
  seo,
  sidebar,
}: DynamicPageProps) => {
  const { title, subtitle } = pageContext
  const supportsArticleView = pageContext.type === 'conditions-page'

  const metadataWithDefaults = {
    ...global?.data?.seo,
    ...seo,
  }

  const navigationData = navigation?.data
  const footerData = footer?.footer

  return (
    <AdditionalDataProvider
      additionalData={{
        ...additionalData,
        pageType: pageContext.type,
      }}
    >
      <Seo {...metadataWithDefaults} />

      {!isNil(navigationData) && <Navigation {...navigationData} />}

      {supportsArticleView ? (
        <>
          <Header subtitle={subtitle} title={title} />
          <ConditionsPageLayout sidebar={sidebar}>
            {sections?.map((section) => {
              if (section.__component.includes('article')) {
                return (
                  <ArticleComponents
                    key={section.id}
                    components={[section] as unknown as ComponentsType[]}
                  />
                )
              }

              return (
                <PageSections
                  key={section.id}
                  components={components}
                  sections={[section]}
                />
              )
            })}
          </ConditionsPageLayout>
        </>
      ) : (
        <PageSections components={components} sections={sections} />
      )}

      {!isNil(footerData) && <Footer {...footerData} />}
    </AdditionalDataProvider>
  )
}

const getPageWithFallback = async (url: string) => {
  let pageData = await getConditionsPage(url)

  if (pageData.data) {
    const sidebarData = await getConditionsSection()
    const rootPageData = await getConditionsRootPage()

    return {
      data: {
        ...pageData.data,
        title: pageData.data.title || rootPageData.title,
        subtitle: pageData.data.subtitle || rootPageData.subtitle,
      },
      source: 'conditions-page',
      sidebar: sidebarData.data,
    }
  }

  pageData = await getPage(url)

  if (pageData.data) {
    return {
      data: pageData.data,
      source: 'page',
    }
  }

  return {
    data: null,
    source: null,
  }
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { params, req, query } = context

  const proto = getHeader(req, 'x-forwarded-proto') ?? 'http'
  const host = getHeader(req, 'x-forwarded-host') ?? req.headers.host ?? ''
  const url = [params?.path ?? ''].flat().join('/')
  const pageUrl = `/${url}`
  const origin = `${proto}://${host}`

  const envConfig = getEnvConfig(host)
  const publicAPI = envConfig.ADDITIONAL_DATA_API_ENDPOINT
  const region = envConfig.region

  const { data: pageData, source, sidebar } = await getPageWithFallback(pageUrl)
  const staticTexts = await getStaticTexts()
  const showSignUpModal = query.modal === RewriteSlugs.signUpModal.queryKey

  if (!pageData) {
    return { notFound: true }
  }

  const {
    seo,
    path,
    navigation: navigationReference,
    footer,
    contentSections,
    components,
  } = pageData

  let allSections = [contentSections || [], components || []].flat()

  // Auto-rates: only run when the page has the auto-rates configuration component.
  const autoRatesComponentConfig = getAutoRatesComponentConfig(allSections)
  let pageAutoRatesConfigData

  if (autoRatesComponentConfig) {
    pageAutoRatesConfigData = await fetchAutoRatesRepresentativeExamplesData(
      autoRatesComponentConfig,
      publicAPI
    )

    if (pageAutoRatesConfigData && !isEmpty(pageAutoRatesConfigData)) {
      allSections = await replaceAutoRatesPlaceholders(
        allSections,
        pageAutoRatesConfigData,
        region
      )
    }
  }

  // Footer auto-rates: reuse page config when possible (same product + same amount), otherwise fetch.
  const footerAutoRatesProductsConfig = (footer as FooterResponse | undefined)
    ?.footer?.footerAutoRatesProductConfig?.autoRatesProductConfig
  const hasFooterAutoRates =
    footerAutoRatesProductsConfig && !isEmpty(footerAutoRatesProductsConfig)

  let footerAutoRatesConfigData
  if (hasFooterAutoRates) {
    footerAutoRatesConfigData = await resolveFooterAutoRatesConfigData(
      footerAutoRatesProductsConfig,
      pageAutoRatesConfigData,
      publicAPI
    )
  }
  const footerResult =
    hasFooterAutoRates && footer && footerAutoRatesConfigData
      ? await replaceAutoRatesPlaceholders(
          footer,
          footerAutoRatesConfigData,
          region
        )
      : footer

  let hasMediaArticleList: boolean | undefined
  let hasDeferralCalculator: boolean | undefined
  let hasInstallmentsCalculator: boolean | undefined
  const cashbackSections: PageContentSectionsItem[] = []
  const eshopSections: EshopCategory[] = []
  let sections: Array<PageComponentsItem | PageContentSectionsItem> = []
  const orderedComponents: ComponentType[] = []

  allSections.forEach((section) => {
    // TODO: Remove these once we migrate production layout to new components structure
    if (section.__component?.startsWith('sections.')) {
      sections.push(section)
    }
    if (
      section.__component?.startsWith('essentials.') ||
      section.__component?.startsWith('specialized.') ||
      section.__component?.startsWith('page-specific.')
    ) {
      orderedComponents.push(section as unknown as ComponentType)
    }

    switch (section.__component) {
      case 'sections.eshops':
      case 'specialized.eshops':
        if (section.category) {
          eshopSections.push(section.category)
        }
        break
      case 'sections.cashback':
        cashbackSections.push(section)
        break
      case 'sections.media-article-list':
        hasMediaArticleList = true
        break
      case 'sections.deferral-calculator':
      case 'specialized.deferral-calculator':
        hasDeferralCalculator = true
        break
      case 'sections.installments-calculator':
      case 'specialized.installments-calculator':
        hasInstallmentsCalculator = true
        break
      default:
        break
    }
  })

  const eshopsData = eshopSections.length
    ? (
        await Promise.all(
          eshopSections.map((category) =>
            getEshops('list', CATEGORY_TO_INCLUDE_PARAM[category])
          )
        )
      ).reduce((acc, curr) => ({ ...acc, ...curr }), {})
    : {}

  const mediaArticles = hasMediaArticleList ? await getMediaArticles() : null

  let deferralCalculatorConfig = null
  if (hasDeferralCalculator) {
    deferralCalculatorConfig =
      pageAutoRatesConfigData?.['deferred-payment-config'] ??
      (await fetchDeferredCalcConfig(publicAPI, region))
  }

  let installmentsCalculatorConfig = null
  if (hasInstallmentsCalculator) {
    installmentsCalculatorConfig =
      pageAutoRatesConfigData?.['installments-config'] ??
      (await fetchInstallmentsCalcConfig(publicAPI, region))
  }

  const cashbackData = cashbackSections.length
    ? (await Promise.all(cashbackSections.map(() => getBenefits()))).reduce(
        (acc, curr) => ({ ...acc, ...curr }),
        {}
      )
    : {}

  const isConditionsPage = source === 'conditions-page'

  sections = isConditionsPage
    ? allSections
    : allSections.filter((section) =>
        section.__component?.startsWith('sections.')
      )

  const pageContext = {
    path,
    type: source,
    title: isConditionsPage ? pageData.title : null,
    subtitle: isConditionsPage ? pageData.subtitle : null,
    isRoot: isConditionsPage ? pageData.isRoot : null,
  }

  const navigationId = navigationReference?.id
  const navigation = navigationId ? await getNavigation(navigationId) : null

  return {
    props: {
      additionalData: {
        cashbackData,
        eshopsData,
        mediaArticlesData: mediaArticles?.data ?? null,
        origin,
        signUpModalOpened: showSignUpModal,
        deferralCalculatorConfig,
        installmentsCalculatorConfig,
        staticTexts: staticTexts.data ?? null,
      },
      components: orderedComponents,
      footer: footerResult ?? null,
      navigation,
      pageContext,
      sections,
      seo: seo ?? null,
      sidebar: sidebar ?? null,
    },
  }
}

export default DynamicPage
